import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, UserPlus, LayoutGrid, Bookmark, Search, 
  TrendingUp, Clock, Mail, Building, ArrowRight,
  Loader2, Send, CheckCircle2, XCircle, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'stone' }) => (
  <div className="bg-white border border-stone-200 p-6">
    <div className="flex items-start justify-between mb-4">
      <div className={`p-3 bg-${color}-100 rounded-lg`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
    <h3 className="text-3xl font-semibold text-stone-900 mb-1">{value?.toLocaleString() || 0}</h3>
    <p className="text-sm text-stone-500">{title}</p>
    {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
  </div>
);

// Invite Professional Modal
const InviteProfessionalModal = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'architect',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/rep/invite`, formData, { withCredentials: true });
      toast.success(`Invito inviato a ${formData.email}`);
      setOpen(false);
      setFormData({ first_name: '', last_name: '', email: '', role: 'architect', company: '', message: '' });
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'invito');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-stone-900 hover:bg-stone-800" data-testid="invite-professional-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Invita Professionista
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">Invita un Professionista</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <p className="text-sm text-stone-600">
            Invita architetti e designer a unirsi alla piattaforma. Potrai monitorare la loro attività.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block text-sm">Nome</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="Mario"
                required
              />
            </div>
            <div>
              <Label className="mb-2 block text-sm">Cognome</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Rossi"
                required
              />
            </div>
          </div>
          
          <div>
            <Label className="mb-2 block text-sm">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="architetto@studio.com"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block text-sm">Ruolo</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="architect">Architetto</SelectItem>
                  <SelectItem value="interior_designer">Interior Designer</SelectItem>
                  <SelectItem value="design_studio">Design Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block text-sm">Azienda</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Studio Rossi"
              />
            </div>
          </div>
          
          <div>
            <Label className="mb-2 block text-sm">Messaggio (opzionale)</Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Ti invito a scoprire la nostra piattaforma..."
              className="min-h-[80px]"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full bg-stone-900 hover:bg-stone-800"
            disabled={loading}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Invia Invito</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Invitee Card Component
const InviteeCard = ({ invitee, onClick }) => {
  const isRegistered = invitee.status === 'accepted';
  const roleLabels = {
    architect: 'Architetto',
    interior_designer: 'Interior Designer',
    design_studio: 'Design Studio'
  };
  
  return (
    <div 
      className={`bg-white border ${isRegistered ? 'border-stone-200' : 'border-dashed border-stone-300'} p-5 cursor-pointer hover:shadow-md transition-shadow`}
      onClick={onClick}
      data-testid={`invitee-card-${invitee.email}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isRegistered ? 'bg-green-100' : 'bg-stone-100'}`}>
            {isRegistered ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <Clock className="w-5 h-5 text-stone-400" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-stone-900">
              {invitee.first_name} {invitee.last_name}
            </h3>
            <p className="text-sm text-stone-500">{invitee.email}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${isRegistered ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          {isRegistered ? 'Registrato' : 'In attesa'}
        </span>
      </div>
      
      <div className="flex items-center gap-4 text-sm text-stone-500 mb-3">
        <span className="capitalize">{roleLabels[invitee.role] || invitee.role}</span>
        {invitee.company && (
          <>
            <span className="text-stone-300">|</span>
            <span className="flex items-center gap-1">
              <Building className="w-3 h-3" /> {invitee.company}
            </span>
          </>
        )}
      </div>
      
      {isRegistered && (
        <div className="flex items-center gap-6 pt-3 border-t border-stone-100">
          <div className="text-center">
            <p className="text-lg font-semibold text-stone-900">{invitee.projects_count || 0}</p>
            <p className="text-xs text-stone-500">Progetti</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-stone-900">{invitee.saved_count || 0}</p>
            <p className="text-xs text-stone-500">Salvati</p>
          </div>
          {invitee.last_activity && (
            <div className="ml-auto text-right">
              <p className="text-xs text-stone-500">Ultima attività</p>
              <p className="text-sm text-stone-700">
                {new Date(invitee.last_activity).toLocaleDateString('it-IT')}
              </p>
            </div>
          )}
        </div>
      )}
      
      {!isRegistered && (
        <div className="pt-3 border-t border-stone-100">
          <p className="text-xs text-stone-400">
            Invitato il {new Date(invitee.invited_at).toLocaleDateString('it-IT')}
          </p>
        </div>
      )}
    </div>
  );
};

// Activity Detail Modal
const ActivityDetailModal = ({ invitee, open, onClose }) => {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invitee?.user_id) {
      fetchActivity();
    }
  }, [open, invitee]);

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/rep/invitees/${invitee.user_id}/activity`, { withCredentials: true });
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!invitee) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">
            Attività di {invitee.first_name} {invitee.last_name}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          </div>
        ) : activity ? (
          <div className="space-y-6 mt-4">
            {/* User Info */}
            <div className="flex items-center gap-4 p-4 bg-stone-50">
              <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center">
                <Users className="w-6 h-6 text-stone-600" />
              </div>
              <div>
                <h3 className="font-medium">{activity.user?.first_name} {activity.user?.last_name}</h3>
                <p className="text-sm text-stone-500">{activity.user?.email}</p>
                <p className="text-xs text-stone-400">
                  Membro dal {new Date(activity.activity?.member_since).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white border border-stone-200">
                <LayoutGrid className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                <p className="text-2xl font-semibold">{activity.activity?.projects?.total || 0}</p>
                <p className="text-xs text-stone-500">Progetti</p>
                <p className="text-xs text-green-600 mt-1">+{activity.activity?.projects?.this_week || 0} questa settimana</p>
              </div>
              <div className="text-center p-4 bg-white border border-stone-200">
                <Bookmark className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                <p className="text-2xl font-semibold">{activity.activity?.saved_materials?.total || 0}</p>
                <p className="text-xs text-stone-500">Materiali salvati</p>
                <p className="text-xs text-green-600 mt-1">+{activity.activity?.saved_materials?.this_week || 0} questa settimana</p>
              </div>
              <div className="text-center p-4 bg-white border border-stone-200">
                <Search className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                <p className="text-2xl font-semibold">{activity.activity?.visual_searches?.total || 0}</p>
                <p className="text-xs text-stone-500">Ricerche visive</p>
                <p className="text-xs text-green-600 mt-1">+{activity.activity?.visual_searches?.this_week || 0} questa settimana</p>
              </div>
              <div className="text-center p-4 bg-white border border-stone-200">
                <Activity className="w-6 h-6 mx-auto mb-2 text-stone-600" />
                <p className="text-2xl font-semibold">{activity.activity?.collections?.total || 0}</p>
                <p className="text-xs text-stone-500">Collezioni</p>
              </div>
            </div>
            
            {/* Recent Projects */}
            {activity.activity?.projects?.recent?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-stone-700 mb-3">Progetti recenti</h4>
                <div className="space-y-2">
                  {activity.activity.projects.recent.map((project, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-stone-50">
                      <div>
                        <p className="font-medium text-sm">{project.name}</p>
                        {project.project_name && (
                          <p className="text-xs text-stone-500">{project.project_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-stone-500">
                          {project.items?.length || 0} materiali
                        </p>
                        <p className="text-xs text-stone-400">
                          {new Date(project.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Last Activity */}
            <div className="text-center text-sm text-stone-500 pt-4 border-t">
              Ultima attività: {new Date(activity.activity?.last_activity).toLocaleString('it-IT')}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p>Utente non ancora registrato</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Rep Dashboard Component
const RepDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvitee, setSelectedInvitee] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/rep/dashboard`, { withCredentials: true });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch rep dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Accesso non autorizzato. Solo i Rep possono accedere a questa pagina.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleInviteeClick = (invitee) => {
    if (invitee.status === 'accepted' && invitee.user_id) {
      setSelectedInvitee(invitee);
      setShowActivityModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Breadcrumb 
            items={[{ label: 'Dashboard', href: '/dashboard' }]} 
            currentPage="Rep Dashboard" 
          />
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-light text-stone-900">Rep Dashboard</h1>
              <p className="text-stone-500 mt-1">
                Monitora l'attività dei professionisti che hai invitato
              </p>
            </div>
            <InviteProfessionalModal onSuccess={fetchDashboard} />
          </div>
          
          {/* Overview Stats */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard 
                title="Totale Inviti" 
                value={dashboardData?.overview?.total_invitations}
                icon={Mail}
              />
              <StatCard 
                title="Registrati" 
                value={dashboardData?.overview?.total_registered}
                subtitle={`${dashboardData?.overview?.conversion_rate || 0}% conversion`}
                icon={CheckCircle2}
                color="green"
              />
              <StatCard 
                title="In Attesa" 
                value={dashboardData?.overview?.pending_invitations}
                icon={Clock}
                color="amber"
              />
              <StatCard 
                title="Attivi questa settimana" 
                value={dashboardData?.overview?.active_this_week}
                icon={Activity}
                color="gold"
              />
              <StatCard 
                title="Progetti Totali" 
                value={dashboardData?.aggregate_stats?.total_projects}
                subtitle={`${dashboardData?.aggregate_stats?.total_saved_materials || 0} materiali salvati`}
                icon={LayoutGrid}
              />
            </div>
          </section>
          
          {/* Invitees List */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-medium text-stone-900 flex items-center gap-2">
                <Users className="w-5 h-5" /> I tuoi invitati
              </h2>
              {dashboardData?.invitees?.length > 20 && (
                <span className="text-sm text-stone-500">
                  Mostrando 20 di {dashboardData?.overview?.total_invitations}
                </span>
              )}
            </div>
            
            {dashboardData?.invitees?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardData.invitees.map((invitee) => (
                  <InviteeCard 
                    key={invitee.invitation_id || invitee.email}
                    invitee={invitee}
                    onClick={() => handleInviteeClick(invitee)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-stone-200">
                <UserPlus className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-xl font-medium text-stone-900 mb-2">Nessun invito inviato</h3>
                <p className="text-stone-500 mb-6 max-w-md mx-auto">
                  Inizia a invitare architetti e designer per costruire la tua rete di professionisti.
                </p>
                <InviteProfessionalModal onSuccess={fetchDashboard} />
              </div>
            )}
          </section>
        </div>
      </main>
      
      <Footer />
      
      {/* Activity Detail Modal */}
      <ActivityDetailModal 
        invitee={selectedInvitee}
        open={showActivityModal}
        onClose={() => setShowActivityModal(false)}
      />
    </div>
  );
};

export default RepDashboard;
